// utils/axios.js
import axios from "axios";
import { authService } from "services/authService";
import { getStore } from "store/reducers";
import { SET_TOKEN, SET_ERROR } from "store/reducers/actions";

const debug = false;

class AxiosService {
  instance;
  store;
  publicRoutes = [
    "/api/auth/logout",
    "/api/auth/login",
    "/api/auth/register",
    "/api/auth/verify-otp",
    "/api/auth/forgot-password",
    "/api/auth/refresh",
  ];
  constructor() {
    this.instance = axios.create({
      baseURL: import.meta.env.VITE_APP_API_URL,
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    this.store = getStore();
    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor
    this.instance.interceptors.request.use(
      (config) => {
        // Check if the route needs authentication
        const isPublicRoute = this.publicRoutes.some((route) =>
          config.url.includes(route),
        );

        if (!isPublicRoute) {
          const state = this.store.getState();
          const accessToken = state.auth.accessToken;

          if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
          }
        }

        return config;
      },
      (error) => {
        this.store.dispatch({
          type: SET_ERROR,
          payload: "Request configuration error",
        });
        return Promise.reject(error);
      },
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Don't retry refresh token requests
        // if (originalRequest.url?.includes('/api/auth/refresh/')) {
        //   console.error('🔴 Refresh token request failed:', error.response?.data);
        //   // this.store.dispatch({ type: LOGOUT });
        //   return Promise.reject(error);
        // }

        const isPublicRoute = this.publicRoutes.some((route) =>
          originalRequest.url.includes(route),
        );

        // Don't retry public routes or already retried requests
        if (isPublicRoute || originalRequest._retry) {
          return Promise.reject(error);
        }

        // Handle token expiration
        if (this.isTokenExpiredError(error) && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const { data } = await this.refreshToken();

            if (data.success && data.access_token) {
              // Update store with new token
              this.store.dispatch({
                type: SET_TOKEN,
                payload: { accessToken: data.access_token },
              });

              // Update axios default headers
              this.instance.defaults.headers.common["Authorization"] =
                `Bearer ${data.access_token}`;

              // Update original request and retry
              originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
              return this.instance(originalRequest);
            }
          } catch (refreshError) {
            debug &&
              console.error("❌ Refresh failed:", refreshError.response?.data);
            // this.store.dispatch({ type: LOGOUT });
            if (!originalRequest.url.includes("/api/auth/logout")) {
              await authService.logout();
            }
            return Promise.reject(refreshError);
          }
        }

        // Handle other errors
        return Promise.reject(error);
      },
    );
  }

  isTokenExpiredError(error) {
    // Only 401 means expired/invalid credentials. Treating 403 as expiry
    // retried multipart uploads (FormData) and turned file fields into strings.
    return error.response?.status === 401;
  }

  async refreshToken() {
    delete this.instance.defaults.headers.common["Authorization"];
    return this.instance.post("/api/auth/refresh/");
  }

  // handleRefreshFailure(error) {
  //   console.error('Token refresh failed:', error.response?.data || error.message);
  //   this.store.dispatch({ type: LOGOUT });
  //   this.store.dispatch({
  //     type: SET_ERROR,
  //     payload: 'Session expired. Please login again.'
  //   });
  // }

  // handleResponseError(error) {
  //   const errorMessage = error.response?.data?.message || error.message;
  //   this.store.dispatch({
  //     type: SET_ERROR,
  //     payload: errorMessage
  //   });
  // }

  // HTTP method wrappers
  async get(url, config = {}) {
    return this.instance.get(url, config);
  }

  async post(url, data = null, config = {}) {
    return this.instance.post(url, data, config);
  }

  async put(url, data = null, config = {}) {
    return this.instance.put(url, data, config);
  }

  async delete(url, config = {}) {
    return this.instance.delete(url, config);
  }

  async patch(url, data = null, config = {}) {
    return this.instance.patch(url, data, config);
  }
}

const axiosService = new AxiosService();
export default axiosService.instance;

// Helper functions for data fetching
export const fetcher = async (args) => {
  const [url, config] = Array.isArray(args) ? args : [args];
  try {
    const response = await axiosService.get(url, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetcherPost = async (args) => {
  const [url, data, config = {}] = Array.isArray(args) ? args : [args];
  try {
    const response = await axiosService.post(url, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// endpoints
export const api = {
  auth: {
    login: (data) => axiosService.post("/api/auth/login/", data),
    logout: () => axiosService.post("/api/auth/logout/"),
    verifyOTP: (data) => axiosService.post("/api/auth/verify-otp/", data),
    refreshToken: () => axiosService.post("/api/auth/refresh/"),
    resetPassword: (data) =>
      axiosService.post("/api/auth/forgot-password/", data),
  },
  user: {
    getProfile: () => axiosService.get("/api/account/profile"),
    updateProfile: (data) => axiosService.put("/api/account/profile", data),
  },
  notifications: {
    getAll: () => axiosService.get("/api/notifications/"),
    getUnread: () => axiosService.get("/api/notifications/unread/"),
    getCount: () => axiosService.get("/api/notifications/count/"),
    markAsRead: (id) =>
      axiosService.post(`/api/notifications/${id}/mark_as_read/`),
    markAllAsRead: () =>
      axiosService.post("/api/notifications/mark_all_as_read/"),
    create: (data) => axiosService.post("/api/notifications/", data),
    delete: (id) => axiosService.delete(`/api/notifications/${id}/`),
  },
  images: {
    getProxiedImage: (imageUrl) => {
      // Use the correct content type for images
      return axiosService.get(
        `/api/proxy-image/?url=${encodeURIComponent(imageUrl)}`,
        {
          responseType: "blob",
        },
      );
    },
  },
  advertising: {
    getBanners: (params = {}) => {
      const queryParams = new URLSearchParams(params).toString();
      return axiosService.get(
        `/api/advertising/banners/${queryParams ? `?${queryParams}` : ""}`,
      );
    },
    getBanner: (id) => axiosService.get(`/api/advertising/banners/${id}/`),
    createBanner: (data) =>
      axiosService.post("/api/advertising/banners/", data, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    updateBanner: (id, data) =>
      axiosService.patch(`/api/advertising/banners/${id}/`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    deleteBanner: (id) =>
      axiosService.delete(`/api/advertising/banners/${id}/`),
    getActiveBanners: (params = {}) => {
      const queryParams = new URLSearchParams(params).toString();
      return axiosService.get(
        `/api/advertising/banners/active_banners/${queryParams ? `?${queryParams}` : ""}`,
      );
    },
    getBannerTypes: () =>
      axiosService.get("/api/advertising/banners/banner_types/"),
    recordImpression: (id, sessionId) =>
      axiosService.post(`/api/advertising/banners/${id}/record_impression/`, {
        session_id: sessionId,
      }),
    recordClick: (id, sessionId) =>
      axiosService.post(`/api/advertising/banners/${id}/record_click/`, {
        session_id: sessionId,
      }),
    getAnalytics: (params = {}) => {
      const queryParams = new URLSearchParams(params).toString();
      return axiosService.get(
        `/api/advertising/banners/analytics/${queryParams ? `?${queryParams}` : ""}`,
      );
    },
  },
};
